import { useEffect, useRef } from 'react';
import { UseFormReset } from 'react-hook-form';

import cloneDeep from 'lodash/cloneDeep';

import { IArrestFormData, getDefaultFormValues } from '../types';

interface UseArrestFormInitializationProps {
  currentArrestee: any;
  otherArrests: { arrestId: string; masterNameId: string }[];
  reset: UseFormReset<IArrestFormData>;
}

interface UseArrestFormInitializationReturn {
  originalArresteeRef: React.MutableRefObject<IArrestFormData | null>;
  originalArresteeFullRef: React.MutableRefObject<any>;
  handleCancel: () => void;
}

export const useArrestFormInitialization = ({
  currentArrestee,
  otherArrests,
  reset,
}: UseArrestFormInitializationProps): UseArrestFormInitializationReturn => {
  const originalArresteeRef = useRef<IArrestFormData | null>(null);
  const originalArresteeFullRef = useRef<typeof currentArrestee | null>(null);
  const initialFormLoadedRef = useRef<boolean>(false);
  const currentArresteeIdRef = useRef<string | null>(null);

  // Initialize form with currentArrestee data and auto-select property/evidence fields
  useEffect(() => {
    if (currentArrestee) {
      const currentArresteeId =
        currentArrestee.masterName?._id?.toString() || null;
      const isNewArrestee = currentArresteeId !== currentArresteeIdRef.current;
      const shouldResetForm = !initialFormLoadedRef.current || isNewArrestee;

      if (shouldResetForm) {
        const initialFormData = getDefaultFormValues(currentArrestee);

        // Auto-populate co-defendants with all other arrests in the case by default
        if (
          !initialFormData.coDefendantsArrestNumber ||
          initialFormData.coDefendantsArrestNumber.length === 0
        ) {
          const currentMasterNameId =
            typeof currentArrestee.masterName === 'string'
              ? currentArrestee.masterName
              : currentArrestee.masterName?._id?.toString();

          // Get all other arrest IDs excluding the current arrestee
          const otherArrestIds = otherArrests
            .filter((arrest) => arrest.masterNameId !== currentMasterNameId)
            .map((arrest) => arrest.arrestId);

          initialFormData.coDefendantsArrestNumber = otherArrestIds;
        }

        originalArresteeRef.current = { ...initialFormData };
        // Store the full original arrestee (including arrestCharges) for cancel restoration
        originalArresteeFullRef.current = cloneDeep(currentArrestee);
        reset(initialFormData);

        // Mark as loaded and track current arrestee ID
        initialFormLoadedRef.current = true;
        currentArresteeIdRef.current = currentArresteeId;
      }
    }
  }, [currentArrestee, otherArrests, reset]);

  const handleCancel = () => {
    if (originalArresteeRef.current) {
      reset(originalArresteeRef.current, { keepDefaultValues: false });
    }
  };

  return {
    originalArresteeRef,
    originalArresteeFullRef,
    handleCancel,
  };
};
